import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDriverAuth } from '@/hooks/useDriverAuth';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';
import { useRideOfferAlert } from '@/hooks/useRideOfferAlert';
import { useSurgeBroadcast } from '@/hooks/useSurgeBroadcast';
import { useDriverTheme } from '@/hooks/useDriverTheme';
import { toast } from '@/hooks/use-toast';
import { IndianRupee, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { OverlayBridge } from '@/plugins/overlay-bridge';
import { getDriverSessionId, handleDispatchResult } from '@/lib/driverSession';
import SlideToDecline from '@/components/driver/SlideToDecline';

export default function GlobalTripOfferOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { driver, isAuthenticated, refreshDriver } = useDriverAuth();
  const { tokens, isDark } = useDriverTheme();

  const [incomingRequest, setIncomingRequest] = useState<any>(null);
  const [requestCountdown, setRequestCountdown] = useState(0);
  const [respondingRequest, setRespondingRequest] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [responseKind, setResponseKind] = useState<'accept' | 'cancel' | null>(null);
  // Silent-accept mode: when the driver tapped Accept in the native
  // background heads-up tile, we receive `action=accept` in the URL and
  // must NOT flash the in-app trip offer card before navigating to the
  // Trip Accepted screen.
  const [silentAccept, setSilentAccept] = useState(false);


  const incomingRequestRef = useRef(incomingRequest);
  const isAvailableRef = useRef(driver?.is_available ?? false);

  useEffect(() => { incomingRequestRef.current = incomingRequest; }, [incomingRequest]);
  useEffect(() => { isAvailableRef.current = driver?.is_available ?? false; }, [driver?.is_available]);

  useRideOfferAlert(!!incomingRequest);

  // Tell the native FcmReceiver that the JS overlay path is live — it
  // will then SKIP launching the full-screen TripOfferActivity for
  // incoming `trip_offer` pushes, leaving the in-app overlay (driven by
  // realtime + deep-link recovery) to handle them. We mirror the
  // `shouldSubscribe` condition so background/killed states (where the
  // JS realtime channel is gone) correctly fall back to the native popup.

  // P2: Surge broadcast — re-acknowledge offer to pull fresh fare if a surge change lands while an offer is open
  const surge = useSurgeBroadcast();
  useEffect(() => {
    if (!surge || !incomingRequestRef.current || !driver?.id) return;
    // Re-fetch the offer to surface any updated fare
    supabase.functions
      .invoke('dispatch-trip', { body: { action: 'check_offer', driver_id: driver.id } })
      .then((res) => {
        if (res.data?.has_offer && res.data.offer && incomingRequestRef.current?.id === res.data.offer.id) {
          setIncomingRequest((prev: any) => (prev ? { ...prev, ...res.data.offer } : prev));
        }
      })
      .catch(() => {});
  }, [surge, driver?.id]);

  const isDriverRoute = location.pathname.startsWith('/driver/');
  const isAvailable = driver?.is_available ?? false;
  const shouldSubscribe = isAuthenticated && isAvailable && isDriverRoute;

  // Track whether the app is actually in the foreground. React state alone
  // is not enough — `shouldSubscribe` stays true while the app is merely
  // backgrounded / locked, which would leave OverlayState=true and cause
  // FcmReceiver to skip the native full-screen popup precisely when it's
  // needed. We mirror the real Capacitor App state instead.
  const [appForeground, setAppForeground] = useState(true);
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    let listener: any = null;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const initial = await App.getState();
        if (!cancelled) setAppForeground(!!initial.isActive);
        listener = await App.addListener('appStateChange', ({ isActive }) => {
          setAppForeground(!!isActive);
        });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; try { listener?.remove?.(); } catch { /* ignore */ } };
  }, []);

  // Mirror the COMBINED (foreground + driver-route + auth + available) state
  // into the native OverlayState flag. When true, FcmReceiver SKIPS launching
  // the full-screen TripOfferActivity because the in-app overlay will
  // surface the offer. When the app is backgrounded / killed / locked /
  // off a /driver/* route, this is false and the native popup takes over.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    const active = shouldSubscribe && appForeground;
    OverlayBridge.setOverlayActive({ active }).catch(() => {});
    return () => {
      OverlayBridge.setOverlayActive({ active: false }).catch(() => {});
    };
  }, [shouldSubscribe, appForeground]);

  // Listen for accept / reject coming back from the native full-screen
  // TripOfferActivity (when the popup was shown over the lock screen /
  // over other apps). The native side already deep-links into
  // /driver/home?offer=<id>&action=<accept|reject>, but we also surface
  // the event live in case the app is already in the foreground.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    let handle: any = null;
    OverlayBridge.addListener('offerAction', (ev) => {
      const url = `/driver/home?offer=${encodeURIComponent(ev.request_id)}&action=${ev.action}`;
      navigate(url);
    }).then((h) => { handle = h; }).catch(() => {});
    return () => { try { handle?.remove?.(); } catch { /* ignore */ } };
  }, [navigate]);

  // FCM heads-up Accept / Decline action buttons launch MainActivity with a
  // VIEW intent whose data is `https://localhost/driver/home?offer=<id>&action=<accept|reject>`.
  // Capacitor's App plugin surfaces that as `appUrlOpen`. Without this
  // listener the query string is dropped on cold start / resume and the
  // trip-request status is never updated. We forward the relative path
  // into React Router so the existing `pendingNativeActionRef` drain
  // fires `handleAccept` / `handleCancel`.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    let handle: any = null;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const h = await App.addListener('appUrlOpen', ({ url }) => {
          try {
            const u = new URL(url);
            if (!u.pathname.startsWith('/driver/')) return;
            navigate(`${u.pathname}${u.search}${u.hash}`);
          } catch { /* ignore malformed url */ }
        });
        if (cancelled) { try { h.remove(); } catch { /* ignore */ } }
        else handle = h;
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; try { handle?.remove?.(); } catch { /* ignore */ } };
  }, [navigate]);

  const acknowledgeOffer = useCallback(async (offer: any) => {
    if (!driver?.id || acknowledging) return;
    setAcknowledging(true);
    try {
      const res = await supabase.functions.invoke('dispatch-trip', {
        body: { action: 'acknowledge_offer', request_id: offer.id, driver_id: driver.id, session_id: getDriverSessionId() },
      });
      handleDispatchResult(res);
      // Admin dispatch-trip is the single writer for the countdown.
      // Only apply the echoed `offer_expires_at`; a non-success response is a
      // no-op and must never clear the value coming from realtime.
      if (res.data?.success && res.data?.offer_expires_at) {
        setIncomingRequest((prev: any) => prev ? {
          ...prev,
          offer_expires_at: res.data.offer_expires_at,
          timer_started_at: res.data.timer_started_at,
        } : null);
      }
    } catch (err) {
      console.error('Acknowledge offer error:', err);
    }
    setAcknowledging(false);
  }, [driver?.id, acknowledging]);

  useEffect(() => {
    if (!driver?.id || !shouldSubscribe) return;

    const channel = supabase
      .channel(`global-offer-${driver.id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'trip_requests',
        filter: `offered_driver_id=eq.${driver.id}`,
      }, async (payload) => {
        const row = payload.new as any;
        if (!row) return;
        const current = incomingRequestRef.current;

        // Not an active offer for us any more → clear the card if it's the
        // one on screen, then stop.
        if (row.status !== 'offered' || row.offered_driver_id !== driver.id) {
          if (current && row.id === current.id) setIncomingRequest(null);
          return;
        }

        // An 'offered' row addressed to this driver ALWAYS surfaces — this is
        // attempt-agnostic on purpose. A re-offer of the same request (2nd /
        // 3rd attempt after a timeout) arrives as another UPDATE with the same
        // id and a fresh offer_expires_at; it must re-open the card even though
        // the previous attempt expired. We also do not gate on
        // drivers.is_available: the server already decided this driver is
        // eligible, and a stale local availability flag must never swallow an
        // offer.
        const isSameAttempt =
          current &&
          current.id === row.id &&
          current.offer_expires_at === row.offer_expires_at;
        if (isSameAttempt) {
          // Same attempt, unrelated column change — merge quietly, no re-alert.
          setIncomingRequest((prev: any) => (prev ? { ...prev, ...row } : row));
          return;
        }

        // New offer, or a NEW attempt on the same request → full re-show.
        setSilentAccept(false);
        setIncomingRequest(row);
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      })

      .subscribe((status) => {
        // Realtime is the SOLE delivery path for on-demand offers. No fallback
        // read is performed — if the channel is unhealthy, the offer is missed
        // and the dispatch edge function will roll to the next driver via
        // acceptanceTimer. Reconnection is handled by useRealtimeHealth.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn('[offer-overlay] Realtime channel unhealthy:', status);
        }
      });
    return () => { supabase.removeChannel(channel); };
  }, [driver?.id, shouldSubscribe]);

  // Deep-link recovery: when the user taps the FCM heads-up notification, the
  // app opens at /driver/home?offer=<request_id>. Realtime cannot replay a
  // postgres_changes event that fired before the channel re-subscribed
  // (cold start, killed app, locked screen), so we one-shot-fetch the offer
  // via `check_offer` and seed the overlay manually. Realtime continues to be
  // the live path for all other cases.
  useEffect(() => {
    const offerId = searchParams.get('offer');
    if (!offerId) return;
    if (!driver?.id) return;
    if (incomingRequestRef.current) {
      const next = new URLSearchParams(searchParams);
      next.delete('offer');
      setSearchParams(next, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await supabase.functions.invoke('dispatch-trip', {
          body: { action: 'check_offer', driver_id: driver.id },
        });
        if (cancelled) return;
        const o = res.data?.offer;
        if (res.data?.has_offer && o && o.id === offerId) {
          setIncomingRequest(o);
          if (typeof navigator !== 'undefined' && (navigator as any).vibrate) {
            (navigator as any).vibrate([200, 100, 200]);
          }
        } else {
          // Offer expired or re-offered to someone else before the app opened.
          // Clear silentAccept and the pending native action so future offers
          // are not permanently hidden for the rest of this session.
          setSilentAccept(false);
          pendingNativeActionRef.current = null;
        }
      } catch (err) {
        console.warn('[offer-overlay] deep-link check_offer failed', err);
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('offer');
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, driver?.id]);

  useEffect(() => {
    if (incomingRequest && !incomingRequest.timer_started_at) {
      acknowledgeOffer(incomingRequest);
    }
    // Keyed on the ATTEMPT (id + expiry), not just the id, so a re-offer of
    // the same request is acknowledged again and gets its own countdown.
  }, [incomingRequest?.id, incomingRequest?.offer_expires_at]);


  // Native popup deep-link: when the driver tapped Accept / Decline in the
  // full-screen TripOfferActivity, the URL arrives as
  // /driver/home?offer=<id>&action=<accept|reject>. Stash the requested
  // action so the post-handler effect (declared below, after handleAccept
  // / handleCancel exist) can auto-trigger it once the offer is seeded.
  const pendingNativeActionRef = useRef<{ id: string; action: string } | null>(null);
  useEffect(() => {
    const action = searchParams.get('action');
    const offerId = searchParams.get('offer');
    if (!action || !offerId) return;
    pendingNativeActionRef.current = { id: offerId, action };
    if (action === 'accept') setSilentAccept(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!incomingRequest?.offer_expires_at) { setRequestCountdown(0); return; }
    const requestId = incomingRequest.id;
    const driverId = driver?.id;
    // Guard key includes the expiry so each ATTEMPT on the same request can
    // fire its own timeout reject (attempt 2/3 were previously swallowed).
    const attemptKey = `${requestId}:${incomingRequest.offer_expires_at}`;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(incomingRequest.offer_expires_at).getTime() - Date.now()) / 1000));
      setRequestCountdown(remaining);
      if (remaining <= 0) {
        setIncomingRequest(null);
        // Fire reject immediately so the server moves to the next driver
        // without waiting for the pg_cron drain (up to 60s). Uses a
        // separate ref so a concurrent accept/cancel tap cannot block it.
        if (requestId && driverId && timeoutFiredRef.current !== attemptKey) {
          timeoutFiredRef.current = attemptKey;
          supabase.functions
            .invoke('dispatch-trip', {
              body: { action: 'reject', request_id: requestId, driver_id: driverId, session_id: getDriverSessionId() },
            })
            .catch(() => {
              // Non-fatal — cron drain is the fallback if this call fails.
            });
        }
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [incomingRequest?.offer_expires_at, incomingRequest?.id, driver?.id]);

  const actionLockRef = useRef(false);
  const timeoutFiredRef = useRef<string | null>(null); // tracks `${request_id}:${offer_expires_at}` of fired timeout reject


  const handleAccept = async () => {
    if (actionLockRef.current || !incomingRequest || !driver?.id || respondingRequest) return;
    actionLockRef.current = true;
    setRespondingRequest(true);
    setResponseKind('accept');
    try {
      const res = await supabase.functions.invoke('dispatch-trip', {
        body: { action: 'accept', request_id: incomingRequest.id, driver_id: driver.id, session_id: getDriverSessionId() },
      });
      handleDispatchResult(res);
      // Strict success: backend MUST return a real booking_id. The legacy
       // pending_booking fallback is gone — anything else is an error.
      if (res.data?.success && res.data?.booking_id) {
        toast({ title: 'Trip accepted!' });
        setIncomingRequest(null);
        window.dispatchEvent(new CustomEvent('driver:booking-changed'));
        // Retry the active-booking refresh briefly to cover propagation lag.
        let tries = 0;
        const retry = setInterval(() => {
          window.dispatchEvent(new CustomEvent('driver:booking-changed'));
          if (++tries >= 4) clearInterval(retry);
        }, 1000);
        navigate('/driver/home');
        refreshDriver();
      } else {
        toast({
          title: 'Could not accept',
          description: res.data?.error || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setRespondingRequest(false);
      setResponseKind(null);
      setSilentAccept(false);
      actionLockRef.current = false;
    }
  };

  const handleCancel = async () => {
    if (actionLockRef.current || !incomingRequest || !driver?.id || respondingRequest) return;
    actionLockRef.current = true;
    setRespondingRequest(true);
    setResponseKind('cancel');
    try {
      const body = { action: 'reject', request_id: incomingRequest.id, driver_id: driver.id, session_id: getDriverSessionId() };
      let rejRes = await supabase.functions.invoke('dispatch-trip', { body });
      // Retry once on network failure. DB safety net still re-offers within ~1s,
      // but the retry improves observability (proper nextOfferDelay + vendor notify).
      if (rejRes?.error) {
        await new Promise((r) => setTimeout(r, 400));
        rejRes = await supabase.functions.invoke('dispatch-trip', { body });
      }
      handleDispatchResult(rejRes);
      toast({ title: 'Trip cancelled' });
      setIncomingRequest(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setRespondingRequest(false);
      setResponseKind(null);
      actionLockRef.current = false;
    }
  };

  // Drain pendingNativeActionRef once the matching offer is loaded.
  useEffect(() => {
    const pending = pendingNativeActionRef.current;
    if (!pending) return;
    if (!incomingRequest || incomingRequest.id !== pending.id) return;
    pendingNativeActionRef.current = null;
    if (pending.action === 'accept') handleAccept();
    else if (pending.action === 'reject') handleCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingRequest?.id]);


  if (!incomingRequest) return null;
  // While silent-accept is in flight, do not render the offer card —
  // handleAccept (triggered by pendingNativeActionRef drain) will
  // navigate to the Trip Accepted screen as soon as dispatch resolves.
  if (silentAccept) return null;

  const TIMER_MAX = Math.max(
    1,
    Number((incomingRequest as any)?.offer_timeout) ||
      Math.round(
        (new Date((incomingRequest as any)?.offer_expires_at).getTime() -
          new Date((incomingRequest as any)?.offered_at ?? Date.now()).getTime()) /
          1000,
      ) || 15,
  );
  const radius = 62;
  const circumference = 2 * Math.PI * radius;
  const progress = requestCountdown / TIMER_MAX;
  const dashOffset = circumference * (1 - progress);
  const fare = incomingRequest.base_fare || 0;
  const distance = incomingRequest.distance_km || 0;
  const tripType = incomingRequest.trip_type || 'Local';
  const vehicleType = incomingRequest.vehicle_type || '';
  const acLabel = incomingRequest.ac_preference === 'non_ac' ? 'Non-AC' : 'AC';
  const durationMin = incomingRequest.duration_minutes || 0;
  const durationHrs = Math.floor(durationMin / 60);
  const durationRemMin = durationMin % 60;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={handleAccept}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Card */}
      <div
        className="relative z-10 w-[90vw] max-w-[360px] max-h-[92dvh] overflow-y-auto rounded-3xl shadow-2xl"
        style={{ background: tokens.bgCard }}
        onClick={() => handleAccept()}
      >
        <div className="px-5 pt-5 pb-4 flex flex-col items-center gap-4">
          {/* Title */}
          <h2 className="text-xl font-extrabold tracking-tight" style={{ color: tokens.textPrimary }}>
            New Trip Offer
          </h2>

          {/* Trip type + vehicle/AC pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span
              className="text-xs font-bold px-4 py-1 rounded-full"
              style={{
                background: isDark ? 'rgba(251,140,60,0.18)' : 'rgba(249,115,22,0.14)',
                color: tokens.textAccent,
              }}
            >
              {tripType}
            </span>
            {vehicleType && (
              <span
                className="text-xs font-semibold px-4 py-1 rounded-full border-[1.5px]"
                style={{ borderColor: tokens.textAccent, color: tokens.textAccent, background: tokens.bgCard }}
              >
                {vehicleType}
              </span>
            )}
            {incomingRequest.ac_preference && (
              <span
                className="text-xs font-semibold px-4 py-1 rounded-full border-[1.5px]"
                style={{ borderColor: tokens.textAccent, color: tokens.textAccent, background: tokens.bgCard }}
              >
                {acLabel}
              </span>
            )}
          </div>

          {/* Circular countdown */}
          <div className="relative flex items-center justify-center" style={{ width: 148, height: 148 }}>
            <svg width="148" height="148" className="-rotate-90">
              <circle cx="74" cy="74" r={radius} fill="none" stroke={isDark ? '#3A2A1F' : '#FCE3CC'} strokeWidth="8" />
              <circle
                cx="74" cy="74" r={radius} fill="none"
                stroke={tokens.textAccent}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                className="transition-all duration-1000 ease-linear"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-3">
              <span className="text-[34px] font-extrabold leading-none" style={{ color: tokens.textAccent }}>
                {requestCountdown}
              </span>
              <span
                className={
                  responseKind
                    ? "text-[9.5px] font-extrabold tracking-[0.08em] mt-1.5 whitespace-nowrap"
                    : "text-[11px] font-extrabold tracking-[0.18em] mt-1.5 whitespace-nowrap"
                }
                style={{ color: tokens.textPrimary }}
              >
                {responseKind === 'accept' ? 'ACCEPTING' : responseKind === 'cancel' ? 'CANCELLING' : 'ACCEPT'}
              </span>
            </div>
          </div>

          {/* Fare + distance/duration card */}
          <div
            className="w-full flex flex-col items-center gap-1 py-3 px-4 rounded-2xl"
            style={{ background: isDark ? 'rgba(251,140,60,0.14)' : 'rgba(249,115,22,0.10)' }}
          >
            <div className="flex items-center gap-0.5">
              <IndianRupee className="h-6 w-6" style={{ color: tokens.textAccent }} strokeWidth={2.5} />
              <span className="text-3xl font-extrabold leading-none" style={{ color: tokens.textAccent }}>
                {Number(fare).toFixed(0)}
              </span>
            </div>
            {(distance > 0 || durationMin > 0) && (
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: tokens.textAccent, opacity: 0.85 }}>
                {distance > 0 && `${Number(distance).toFixed(1)} km`}
                {distance > 0 && durationMin > 0 && ' · '}
                {durationMin > 0 && `${durationHrs}h ${String(durationRemMin).padStart(2, '0')}m`}
              </span>
            )}
          </div>

          {/* Addresses card */}
          <div
            className="w-full rounded-2xl border px-3.5 py-2.5"
            style={{ borderColor: tokens.border }}
          >
            {/* Pickup */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: '#22C55E' }} />
              <p className="text-[13px] font-semibold truncate min-w-0" style={{ color: tokens.textPrimary }}>
                {incomingRequest.pickup_address}
              </p>
            </div>
            {/* Dashed connector */}
            <div className="ml-[5px] my-1 w-px h-4 border-l border-dashed" style={{ borderColor: '#EC4899' }} />
            {/* Drop */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: '#EC4899' }} />
              <p className="text-[13px] font-semibold truncate min-w-0" style={{ color: tokens.textPrimary }}>
                {incomingRequest.dropoff_address}
              </p>
            </div>

            {(incomingRequest.pickup_at || incomingRequest.scheduled_at) && (
              <>
                <div className="border-t my-2" style={{ borderColor: tokens.border }} />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-4 h-4" style={{ color: tokens.textSecondary }} />
                    <span className="text-xs" style={{ color: tokens.textSecondary }}>Pickup</span>
                  </div>
                  <span className="text-xs whitespace-nowrap" style={{ color: tokens.textSecondary }}>
                    {format(new Date(incomingRequest.pickup_at || incomingRequest.scheduled_at), 'd MMM, h:mm a')}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Decline slider — slide the thumb fully across to reject the
              offer. Replaces the tap button to prevent accidental declines.
              The card itself is a tap-to-accept target (onClick={handleAccept}
              on the outer wrapper), so the slider stops pointer/click
              propagation to avoid double-firing accept+reject. */}
          <SlideToDecline
            onDecline={handleCancel}
            disabled={respondingRequest}
            trackBackground={isDark ? 'rgba(251,140,60,0.14)' : 'rgba(249,115,22,0.10)'}
            thumbColor={tokens.textAccent}
            labelColor={tokens.textAccent}
            label="← Slide to reject"
          />
        </div>
      </div>
    </div>
  );
}
